import { cloneElement, useId, useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';

// A short explanation that opens while its trigger (a link, a field's label) is
// hovered or keyboard-focused, and stays open while the pointer is on the tip
// itself (content-management/tooltip). Escape shuts it until the next hover or
// focus. The scope gets the props; CSS in .helptip does the showing.
export function useTip() {
  const id = useId();
  const [shut, setShut] = useState(false);
  const scope = {
    'data-shut': shut || undefined,
    onMouseEnter: () => setShut(false),
    onFocus: () => setShut(false),
    // A shown tip takes the first Escape, so a dialog around it stays open.
    onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Escape' && !shut) { e.stopPropagation(); setShut(true); } },
  };
  return { id, scope, text: (tip: ReactNode) => <span role="tooltip" id={id} className="helptip-text">{tip}</span> };
}

// A tip on one element, such as a link: the tip describes it, so a screen reader reads it too.
export function HelpTip({ tip, children }: { tip: ReactNode; children: ReactElement<{ className?: string }> }) {
  const { id, scope, text } = useTip();
  return (
    <span className="helptip" {...scope}>
      {cloneElement(children, { 'aria-describedby': id, className: `${children.props.className ?? ''} helptip-trigger`.trim() } as Record<string, unknown>)}
      {text(tip)}
    </span>
  );
}
